import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ChangeRow {
  readonly changeId: string;
  readonly changeCode: string;
  readonly title: string;
  readonly changeType: string;
  readonly changeCategory: string;
  readonly status: string;
  readonly priority: string;
  readonly isEmergency: boolean;
  readonly riskRating: string | null;
}

const TYPES = ['', 'STANDARD', 'NORMAL', 'EMERGENCY', 'MAJOR', 'MINOR'];
const CATEGORIES = ['', 'APPLICATION', 'CONFIGURATION', 'RELEASE', 'DATABASE', 'INFRASTRUCTURE', 'SECURITY', 'INTEGRATION', 'OTHER'];
const STATUSES = ['', 'DRAFT', 'SUBMITTED', 'IMPACT_ASSESSMENT', 'APPROVAL_PENDING', 'APPROVED', 'SCHEDULED', 'IMPLEMENTED', 'VALIDATED', 'CLOSED', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CREATE_TYPES = TYPES.filter(Boolean);
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M32 — Change Management Dashboard (§31 / §6-8). Live register of change
 * requests across the ten-state ITIL lifecycle with type, category, priority,
 * emergency flag and derived risk rating. Supports raising a change and driving
 * it through the lifecycle — submit, schedule, implement, validate, close and
 * cancel — plus the BR-02 retrospective review for emergency changes, each wired
 * to the guarded /v1/devops/changes write API. Filterable by change type,
 * category and status.
 */
export function ChangeManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ChangeRow[]>([]);
  const [changeType, setChangeType] = useState('');
  const [changeCategory, setChangeCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('NORMAL');
  const [newCategory, setNewCategory] = useState('APPLICATION');
  const [newPriority, setNewPriority] = useState('MEDIUM');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (changeType) params.set('changeType', changeType);
    if (changeCategory) params.set('changeCategory', changeCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ChangeRow[] }>(`/v1/devops/changes${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, changeType, changeCategory, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.change.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(tr('m32.change.titleRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/changes`, {
        method: 'POST',
        body: { title: newTitle.trim(), changeType: newType, changeCategory: newCategory, priority: newPriority },
        token,
      });
      setNotice(tr('m32.change.raisedNotice', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: ChangeRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/changes/${r.changeId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(tr('m32.change.actNotice', { code: r.changeCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="change-management">
      <h1>{tr('m32.change.title')}</h1>

      <section aria-label="raise-change">
        <h2>{tr('m32.change.raiseTitle')}</h2>
        <form aria-label="change-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.change.fieldTitle')}<input aria-label="field-change-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{tr('m32.change.type')}
            <select aria-label="field-change-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m32.change.category')}
            <select aria-label="field-change-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{tr('m32.change.priority')}
            <select aria-label="field-change-priority" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-change" disabled={busy}>{busy ? tr('m32.change.raising') : tr('m32.change.raiseBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="change-error">{actionError}</p>}
        {notice && <p aria-label="change-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.change.type')}
          <select aria-label="filter-change-type" value={changeType} onChange={(e) => setChangeType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.change.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.change.category')}
          <select aria-label="filter-change-category" value={changeCategory} onChange={(e) => setChangeCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('m32.change.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.change.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.change.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.change.loading')}</p> : (
        <>
          <p aria-label="change-count">{tr('m32.change.count', { count: rows.length })}</p>
          <table aria-label="change-table">
            <thead><tr><th>{tr('m32.change.thCode')}</th><th>{tr('m32.change.thTitle')}</th><th>{tr('m32.change.type')}</th><th>{tr('m32.change.category')}</th><th>{tr('m32.change.priority')}</th><th>{tr('m32.change.thRisk')}</th><th>{tr('m32.change.thEmergency')}</th><th>{tr('m32.change.status')}</th><th>{tr('m32.change.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.changeId} data-status={r.status}>
                  <td>{r.changeCode}</td><td>{r.title}</td><td>{r.changeType}</td><td>{r.changeCategory}</td>
                  <td>{r.priority}</td><td>{r.riskRating ?? '—'}</td>
                  <td>{r.isEmergency ? 'YES' : 'no'}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'DRAFT' && (
                      <button type="button" aria-label={`submit-${r.changeCode}`} onClick={() => void act(r, 'submit', tr('m32.change.act.submitted'))}>{tr('m32.change.submit')}</button>
                    )}
                    {r.status === 'APPROVED' && (
                      <button type="button" aria-label={`schedule-${r.changeCode}`} onClick={() => void act(r, 'schedule', tr('m32.change.act.scheduled'))}>{tr('m32.change.schedule')}</button>
                    )}
                    {r.status === 'SCHEDULED' && (
                      <button type="button" aria-label={`implement-${r.changeCode}`} onClick={() => void act(r, 'implement', tr('m32.change.act.implemented'))}>{tr('m32.change.implement')}</button>
                    )}
                    {r.status === 'IMPLEMENTED' && (
                      <>
                        <button type="button" aria-label={`validate-${r.changeCode}`} onClick={() => void act(r, 'validate', tr('m32.change.act.validated'))}>{tr('m32.change.validate')}</button>
                        {r.isEmergency && (
                          <button type="button" aria-label={`retrospective-${r.changeCode}`} onClick={() => void act(r, 'retrospective-review', tr('m32.change.act.retrospective'))}>{tr('m32.change.retroReview')}</button>
                        )}
                      </>
                    )}
                    {r.status === 'VALIDATED' && (
                      <button type="button" aria-label={`close-${r.changeCode}`} onClick={() => void act(r, 'close', tr('m32.change.act.closed'))}>{tr('m32.change.close')}</button>
                    )}
                    {!['CLOSED', 'CANCELLED', 'VALIDATED'].includes(r.status) && (
                      <button type="button" aria-label={`cancel-${r.changeCode}`} onClick={() => void act(r, 'cancel', tr('m32.change.act.cancelled'))}>{tr('m32.change.cancel')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
